import React, { useState, useRef, useEffect } from "react";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import dayjs from "dayjs";
import { Popover, Tag, Timeline, Tooltip, message } from "antd";
import {
  FileTextOutlined,
  CheckCircleTwoTone,
  ExclamationCircleTwoTone,
  ClockCircleOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  CloseCircleTwoTone,
  SyncOutlined,
  SmileTwoTone,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
  BookOutlined,
  EyeOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import tenderApis from "../../../api/tender/tenderApis";
import Swal from "sweetalert2";

const AllTendersList = () => {
  const [sm, setSm] = useState(false);
  const navigate = useNavigate();
  const [generatedLinks, setGeneratedLinks] = useState<Record<number, string>>(
    {}
  );
  const fallbackTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

  // Cleanup fallback textarea on unmount
  useEffect(() => {
    return () => {
      if (fallbackTextAreaRef.current) {
        document.body.removeChild(fallbackTextAreaRef.current);
        fallbackTextAreaRef.current = null;
      }
    };
  }, []);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      if (!fallbackTextAreaRef.current) {
        const textArea = document.createElement("textarea");
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "0";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        fallbackTextAreaRef.current = textArea;
      }
      const textArea = fallbackTextAreaRef.current;
      textArea.value = text;
      textArea.focus();
      textArea.select();
      const successful = (document as any).execCommand("copy");
      if (!successful) {
        throw new Error("Failed to copy link");
      }
    }
  };

  const buildTenderPublicUrl = (linkId: string) => {
    return `${process.env.REACT_APP_TM_SELLER_APP_URL}/public-links/tender/${linkId}`;
  };

  const generateAndCopyLink = async (record: any) => {
    try {
      const tenderId = record.tenderId;
      console.log("Tender record:", record);

      // Check if linkId already exists in the record from list API
      if (record.linkId) {
        setGeneratedLinks((prev) => ({ ...prev, [tenderId]: record.linkId }));
        await copyToClipboard(buildTenderPublicUrl(record.linkId));
        message.success("Link copied to clipboard!");
        return;
      }

      // Check if link was already generated in this session
      if (generatedLinks[tenderId]) {
        await copyToClipboard(buildTenderPublicUrl(generatedLinks[tenderId]));
        message.success("Link copied to clipboard!");
        return;
      }

      // No existing linkId — generate new link via API
      const response = await tenderApis.generatePublicLink({
        tenderId,
        expiresInDays: 7,
        settings: "{}",
      });

      // Check if response indicates link already exists
      const responseMessage = response?.message || "";
      const isLinkExistsMessage =
        responseMessage.toLowerCase().includes("already") ||
        responseMessage.toLowerCase().includes("exist") ||
        responseMessage.toLowerCase().includes("active link");

      // Try to extract linkId from various possible response structures
      let linkId =
        response?.response?.linkId ||
        response?.linkId ||
        response?.response?.data?.linkId ||
        response?.data?.linkId;

      if (isLinkExistsMessage) {
        if (linkId) {
          setGeneratedLinks((prev) => ({ ...prev, [tenderId]: linkId }));
          await copyToClipboard(buildTenderPublicUrl(linkId));
          message.success("Link copied to clipboard!");
          return;
        } else {
          message.info("Link already exists for this tender.");
          return;
        }
      }

      // Success - new link generated
      if (linkId) {
        setGeneratedLinks((prev) => ({ ...prev, [tenderId]: linkId }));
        await copyToClipboard(buildTenderPublicUrl(linkId));
        message.success("Link generated and copied to clipboard!");
      } else {
        const errorMsg = response?.message || "Failed to generate link";
        Swal.fire("Error", errorMsg, "error");
      }
    } catch (error: any) {
      console.error("Error generating link:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate link";
      if (
        errorMessage?.toLowerCase().includes("already") ||
        errorMessage?.toLowerCase().includes("exist") ||
        errorMessage?.toLowerCase().includes("active link")
      ) {
        message.info("Link already exists for this tender.");
      } else {
        Swal.fire("Error", errorMessage, "error");
      }
    }
  };
  const columns = [
    {
      title: "Tender ID",
      dataIndex: "tenderNumber",
      key: "tenderNumber",
      fixed: "left",
      width: 170,
      render(text: any, record: any) {
        return (
          <>
            <Tooltip title={"Click To View Tender Details"}>
              <span
                className="inlineTableCellLink"
                onClick={() => {
                  if (record.status === "Under Evaluation") {
                    navigate(`/tender-evaluation/${record.tenderId}`);
                  } else if (record.status === "Evaluated") {
                    navigate(`/tender/bafo-analysis/${record.tenderId}`);
                  } else if (record.status === "Awarded") {
                    navigate(`/contracts-view/${record.tenderId}`);
                  } else {
                    navigate(`/tender/view-tender/${record.tenderId}`, {
                      state: {
                        tenderId: record.tenderId,
                        screenName: "All Tenders",
                        status: record.status,
                      },
                    });
                  }
                }}
              >
                {text}
              </span>
            </Tooltip>
          </>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "tenderTitle",
      key: "tenderTitle",
      width: 200,
      render: (text: string) => (
        <Tooltip title={text}>
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 200,
              display: "inline-block",
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Type",
      dataIndex: "tenderType",
      key: "tenderType",
      width: 130,
      render: (type: string) => (
        <Tag
          color={type === "Open" ? "green" : "blue"}
          icon={type === "Open" ? <FolderOpenOutlined /> : <FolderOutlined />}
        >
          {type}
        </Tag>
      ),
    },
    {
      title: "Tender For",
      dataIndex: "tenderFor",
      width: 130,
      key: "tenderFor",
      render: (tenderFor: string) => (
        <Tag color={tenderFor === "Material" ? "purple" : "cyan"}>
          {tenderFor}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: string, record: any) => {
        return (
          <ApprovalTimelineContent OriginalData={record} status={status} />
        );
      },
    },
    {
      title: "Site / Project",
      dataIndex: "siteName",
      key: "siteName",
      width: 200,
      render: (text: string) => (
        <Tooltip title={text}>
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 160,
              display: "inline-block",
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "EOI Deadline",
      dataIndex: "lastDateForExpressingInterest",
      key: "lastDateForExpressingInterest",
      render: (date: any) => date ? dayjs(date).format("DD-MMM-YYYY") : "--",
      width: 120,
    },
    {
      title: "Submission Deadline",
      dataIndex: "lastSubmissionDate",
      key: "lastSubmissionDate",
      render: (date: any) => date ? dayjs(date).format("DD-MMM-YYYY") : "--",
      width: 160,
    },
    {
      title: "Evaluation Start Date",
      dataIndex: "evaluationStartDate",
      key: "evaluationStartDate",
      render: (date: any) => date ? dayjs(date).format("DD-MMM-YYYY") : "--",
      width: 170,
    },
    {
      title: "Generate Link",
      key: "generateLink",
      fixed: "right",
      width: 120,
      align: "center",
      render: (_: any, record: any) => {
        const allowedStatuses = ["Published"];
        if (!allowedStatuses.includes(record.status)) {
          return null;
        }
        return (
          <Tooltip title="Generate and copy tender link">
            <LinkOutlined
              style={{ fontSize: 18, color: "#1890ff", cursor: "pointer" }}
              onClick={() => generateAndCopyLink(record)}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      align: "center",
      render: (_: any, record: any) => (
        <Tooltip title="View Tender">
          <EyeOutlined
            style={{ fontSize: 18, color: "#1890ff", cursor: "pointer" }}
            onClick={() => {
              navigate(`/tender/view-tender/${record.tenderId}`, {
                state: {
                  tenderId: record.tenderId,
                  screenName: "All Tenders",
                  status: record.status,
                },
              });
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const ApprovalTimelineContent = ({ OriginalData, status }: any) => {
    const approvalsData =
      (status === "Pending Approval" && OriginalData?.publishApprovals) ||
      (status === "Under Evaluation" && OriginalData?.evaluationApprovals) ||
      [];
    // console.log("approvalsData", approvalsData, OriginalData); // Keep for debugging if needed

    const statusMap: any = {
      Draft: {
        color: "geekblue",
        icon: <ClockCircleOutlined />,
      },
      Published: { color: "cyan", icon: <BookOutlined /> },
      "Not Interested": {
        color: "default", // or "red", or another suitable color for "Not Interested"
        icon: <CloseCircleTwoTone twoToneColor="#d9d9d9" />,
      },
      "Under Evaluation": { color: "purple", icon: <FileTextOutlined /> },
      Awarded: { color: "orange", icon: <TrophyOutlined /> },
      "Pending Approval": {
        color: "gold", // Ant Design's default for warning/pending
        icon: <ExclamationCircleTwoTone twoToneColor="#faad14" />,
      },
      Approved: {
        color: "green", // Ant Design's default for success
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
      },
      Evaluated: {
        color: "green", // Changed from original '#52c41a' to 'green' for consistency with Ant Design tag colors
        icon: <SyncOutlined />, // TwoTone icon might look better here too if available
      },
      // Add Rejected status if it can appear directly on the main tag as well
      Rejected: {
        color: "red",
        icon: <CloseCircleOutlined />,
      },
    };

    // Get the color and icon based on the current status
    const { color, icon } = statusMap[status] || {
      color: "default", // Fallback color
      icon: <FileTextOutlined />, // Fallback icon
    };

    // Content for the Popover
    const popoverContent = (
      <Timeline className="approval-timeline">
        {approvalsData?.map((approval: any, index: any) => {
          const isApproved = approval.approvalStatus === "Approved";
          const isRejected = approval.approvalStatus === "Rejected";

          return (
            <Timeline.Item
              key={index}
              dot={
                isApproved ? (
                  <CheckCircleOutlined style={{ color: "green" }} />
                ) : isRejected ? (
                  <CloseCircleOutlined style={{ color: "red" }} />
                ) : undefined
              }
            >
              {approval?.userName} - {approval?.approvalStage}
              <div className="designation">{approval?.level}</div>
              {approval?.approvedDate && (
                <div className="designation">
                  {dayjs(approval?.approvedDate).format("DD-MMM-YYYY")}
                </div>
              )}
            </Timeline.Item>
          );
        })}
      </Timeline>
    );

    const tagElement = (
      <Tag color={color} icon={icon}>
        {OriginalData?.status} {/* Display the status from OriginalData */}
      </Tag>
    );

    // Conditionally render Popover or just the Tag
    return status === "Pending Approval" || status === "Under Evaluation" ? (
      <Popover
        content={popoverContent}
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        {tagElement}
      </Popover>
    ) : (
      tagElement
    );
  };
  return (
    <TransactionFlowDefaultTemplate
      moduleKey="site"
      title="Tender List"
      columns={columns}
      selectFilters={[
        {
          title: "Status",
          placeholder: "Select Status",
          options: [
            { value: "All", label: "All" },
            { value: "Draft", label: "Draft" },
            { value: "Pending Approval", label: "Pending Approval" },
            { value: "Approved", label: "Approved" },
            { value: "Published", label: "Published" },
            {
              value: "Evaluated",
              label: "Evaluated",
            },
            { value: "Under Evaluation", label: "Under Evaluation" },
          ],
        },
      ]}
      extra={{
        tableScrollX: 1630,
        dateRange: false,
        excelExport: false,
      }}
    />
  );
};

export default AllTendersList;
